import type { Analysis, AnalysisResultItem } from '@/lib/types';
import type {
  GlobalOrientation,
  LateralizationPattern,
  SopAnalysisResult,
  TestLateralization,
} from '@/components/sop-intake/sop-analyzer/types';

/** Max narrative lines returned for the SOP sidebar tray. */
const MAX_LINES = 4;

export interface SopSidebarSummaryInput {
  sopAnalyzerAnalysis?: SopAnalysisResult | null;
  sopBucketAnalysis?: Analysis | null;
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function lateralizationPatternPhrase(pattern: LateralizationPattern): string {
  switch (pattern) {
    case 'RLP':
      return 'right lateralization preference (RLP)';
    case 'LLP':
      return 'left lateralization preference (LLP)';
    case 'Mixed':
      return 'mixed lateralization';
    case 'Balanced':
    default:
      return 'balanced lateralization';
  }
}

function sagittalPhrase(pattern: GlobalOrientation): string {
  switch (pattern) {
    case 'APT':
      return 'anterior pelvic tilt (APT)';
    case 'SB':
      return 'swayback (SB)';
    case 'Neutral':
    default:
      return 'neutral / stacked alignment';
  }
}

function scoreTestForPriority(t: TestLateralization): number {
  let s = 0;
  if (t.isInvalidated) s += 100;
  if (t.isHypermobile) s += 40;
  if (t.bias === 'Left' || t.bias === 'Right') s += 20;
  return s;
}

function testHasSignal(t: TestLateralization): boolean {
  return (
    t.leftVal !== null ||
    t.rightVal !== null ||
    t.isInvalidated ||
    (t.bias !== null && t.bias !== 'Even') ||
    t.isHypermobile
  );
}

/**
 * Denominator for "X of Y tests": counts tests with a Left/Right/Even bias assigned
 * (matches leftCount + rightCount + evenCount from computeLateralization).
 */
function linesFromSopAnalyzer(res: SopAnalysisResult): string[] {
  const out: string[] = [];
  const lat = res.lateralization;
  const denom = lat.leftCount + lat.rightCount + lat.evenCount;

  if (denom === 0) {
    out.push('No lateral bias signal from the passive SoP battery yet.');
  } else if (lat.pattern === 'Mixed') {
    out.push(
      `${lat.leftCount} test${lat.leftCount === 1 ? '' : 's'} favor left vs ${lat.rightCount} favor right of ${denom} — table reads ${lateralizationPatternPhrase('Mixed')}.`
    );
  } else if (lat.pattern === 'Balanced' || (lat.leftCount === 0 && lat.rightCount === 0)) {
    out.push(
      `All ${denom} lateralization check${denom === 1 ? '' : 's'} read even left-right balance (table: ${lateralizationPatternPhrase('Balanced')}).`
    );
  } else if (lat.pattern === 'RLP') {
    out.push(
      `${lat.rightCount} of ${denom} test${denom === 1 ? '' : 's'} suggest right lateralization (table pattern: ${lateralizationPatternPhrase('RLP')}).`
    );
  } else if (lat.pattern === 'LLP') {
    out.push(
      `${lat.leftCount} of ${denom} test${denom === 1 ? '' : 's'} suggest left lateralization (table pattern: ${lateralizationPatternPhrase('LLP')}).`
    );
  } else {
    out.push(`Table lateralization: ${lateralizationPatternPhrase(lat.pattern)} across ${denom} tests.`);
  }

  const drivers = [...lat.tests]
    .sort((a, b) => scoreTestForPriority(b) - scoreTestForPriority(a))
    .filter(testHasSignal)
    .slice(0, 3)
    .map((t) => t.testLabel);

  const sagittal = sagittalPhrase(res.orientation.pattern);
  let orientationLine = `SoP sagittal reads as ${sagittal}.`;
  if (drivers.length > 0) {
    orientationLine += ` Drivers include ${drivers.join(', ')}.`;
  }
  out.push(orientationLine);

  const flagPool = [...(res.orientation.flags ?? []), ...(res.orientation.hypermobilityFlags ?? [])].filter(Boolean);
  if (flagPool[0] && out.length < MAX_LINES) {
    out.push(truncate(flagPool[0], 120));
  }

  return out.slice(0, MAX_LINES);
}

function dedupeItems(analysis: Analysis): AnalysisResultItem[] {
  const byTest = new Map<string, AnalysisResultItem>();
  const buckets: AnalysisResultItem[][] = [
    ...(analysis.left?.length ? [analysis.left] : []),
    ...(analysis.right?.length ? [analysis.right] : []),
    ...(analysis.intact?.length ? [analysis.intact] : []),
  ];
  for (const arr of buckets) {
    for (const it of arr) {
      if (!byTest.has(it.test)) byTest.set(it.test, it);
    }
  }
  return [...byTest.values()];
}

function linesFromBucketAnalysis(analysis: Analysis): string[] {
  const left = analysis.left ?? [];
  const right = analysis.right ?? [];
  const intact = analysis.intact ?? [];
  const total = left.length + right.length + intact.length;
  if (total === 0) {
    return ['No passive test rows recorded yet.'];
  }

  const lines: string[] = [];
  lines.push(
    `${left.length} test${left.length === 1 ? '' : 's'} skew left-dominant, ${right.length} right-dominant, ${intact.length} relatively balanced (passive battery).`
  );

  const items = dedupeItems(analysis);
  const limitations = items
    .filter((it) => !it.isLeftPassing || !it.isRightPassing || it.isLeftHyper || it.isRightHyper)
    .sort((a, b) => {
      const score = (x: AnalysisResultItem) =>
        (!x.isLeftPassing ? 1 : 0) + (!x.isRightPassing ? 1 : 0) + (x.isLeftHyper || x.isRightHyper ? 1 : 0);
      return score(b) - score(a);
    })
    .slice(0, 3)
    .map((it) => it.test);

  if (limitations.length > 0 && lines.length < MAX_LINES) {
    lines.push(`Limitations include: ${limitations.join(', ')}${limitations.length === 3 && items.length > 3 ? '…' : '.'}`);
  }

  return lines.slice(0, MAX_LINES);
}

/**
 * Short narrative lines for the SOP assessment sidebar (SoP-first summary).
 * Prefers analyzer `SopAnalysisResult`; falls back to legacy bucket `Analysis`.
 */
export function buildSopSidebarSummaryLines(input: SopSidebarSummaryInput): string[] {
  if (input.sopAnalyzerAnalysis) {
    return linesFromSopAnalyzer(input.sopAnalyzerAnalysis);
  }
  if (
    input.sopBucketAnalysis &&
    (input.sopBucketAnalysis.left?.length ||
      input.sopBucketAnalysis.right?.length ||
      input.sopBucketAnalysis.intact?.length)
  ) {
    return linesFromBucketAnalysis(input.sopBucketAnalysis);
  }
  return [];
}
